"use client";

import {
  Container,
  Title,
  Text,
  SimpleGrid,
  Card,
  Stack,
  Group,
  Box,
  Button,
  Divider,
  Accordion,
} from "@mantine/core";
import { IconChevronRight, IconHanger, IconPalette } from "@tabler/icons-react";
import { Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";

export default function SandwichMethodContent() {
  const t = useTranslations("sandwichMethod");
  const faqs = t.raw("faqs") as { question: string; answer: string }[];

  const steps = [
    { title: t("step1Title"), body: t("step1Body") },
    { title: t("step2Title"), body: t("step2Body") },
    { title: t("step3Title"), body: t("step3Body") },
  ];

  return (
    <Container size="md" py="xl">
      {/* Header */}
      <Stack gap="md" mb="xl">
        <Text size="sm" c="dimmed">
          {t("styleGuideLabel")}
        </Text>
        <Title order={1} fz={{ base: 28, md: 40 }}>
          {t("pageTitle")}
        </Title>
        <Text fz="lg" c="dimmed">
          {t("pageSubtitle")}
        </Text>
      </Stack>

      {/* Primary upsell CTA */}
      <Card withBorder p="lg" mb="xl" radius="md" bg="gray.0">
        <Group justify="space-between" wrap="wrap" gap="md">
          <Box maw={460}>
            <Text fw={600} mb={4}>
              {t("ctaTitle")}
            </Text>
            <Text size="sm" c="dimmed">
              {t("ctaBody")}
            </Text>
          </Box>
          <Button
            component={Link}
            href="/ai-outfit-generator"
            rightSection={<IconHanger size={16} />}
            size="md"
          >
            {t("ctaButton")}
          </Button>
        </Group>
      </Card>

      {/* What is it */}
      <Box mb="xl">
        <Title order={2} fz="xl" mb="sm">
          {t("whatIsTitle")}
        </Title>
        <Text c="dimmed">{t("whatIsBody")}</Text>
      </Box>

      {/* Why it works */}
      <Box mb="xl">
        <Title order={2} fz="xl" mb="sm">
          {t("whyWorksTitle")}
        </Title>
        <Text c="dimmed">{t("whyWorksBody")}</Text>
      </Box>

      <Divider my="xl" />

      {/* Steps */}
      <Box mb="xl">
        <Title order={2} fz="xl" mb="lg">
          {t("stepsTitle")}
        </Title>
        <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
          {steps.map((step, i) => (
            <Card key={i} withBorder radius="md" p="md">
              <Text fw={600} mb={6}>
                {step.title}
              </Text>
              <Text size="sm" c="dimmed">
                {step.body}
              </Text>
            </Card>
          ))}
        </SimpleGrid>
      </Box>

      {/* Examples */}
      <Box mb="xl" p="lg" bg="gray.0" style={{ borderRadius: 8 }}>
        <Title order={2} fz="lg" mb="sm">
          {t("examplesTitle")}
        </Title>
        <Text size="sm" c="dimmed">
          {t("examplesBody")}
        </Text>
      </Box>

      {/* Color tie-in */}
      <Card withBorder p="lg" mb="xl" radius="md">
        <Group justify="space-between" wrap="wrap" gap="md">
          <Box maw={460}>
            <Text fw={600} mb={4}>
              {t("colorTitle")}
            </Text>
            <Text size="sm" c="dimmed">
              {t("colorBody")}
            </Text>
          </Box>
          <Button
            component={Link}
            href="/ai-color-analysis"
            variant="light"
            rightSection={<IconPalette size={16} />}
          >
            {t("colorCta")}
          </Button>
        </Group>
      </Card>

      {/* FAQ */}
      <Box mb="xl">
        <Title order={2} fz="xl" mb="md">
          FAQ
        </Title>
        <Accordion variant="separated">
          {faqs.map((faq, i) => (
            <Accordion.Item key={i} value={`faq-${i}`}>
              <Accordion.Control>{faq.question}</Accordion.Control>
              <Accordion.Panel>
                <Text c="dimmed">{faq.answer}</Text>
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      </Box>

      {/* Final CTA */}
      <Box ta="center" py="xl">
        <Title order={2} fz="xl" mb="sm">
          {t("ctaTitle")}
        </Title>
        <Text c="dimmed" mb="lg" maw={520} mx="auto">
          {t("ctaBody")}
        </Text>
        <Group justify="center" gap="md">
          <Button
            component={Link}
            href="/ai-outfit-generator"
            size="md"
            rightSection={<IconChevronRight size={16} />}
          >
            {t("ctaButton")}
          </Button>
        </Group>
        <Text size="sm" c="dimmed" mt="lg">
          {t("secondaryCtaText")}{" "}
          <Link href="/ai-color-analysis">{t("secondaryCtaButton")}</Link>
        </Text>
      </Box>
    </Container>
  );
}
